import { useState } from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import { ArrowLeftIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import {
  usePdfTemplates,
  usePdfTemplateVersions,
  useCreatePdfTemplateDraft,
} from '@/api/rpc/hooks/communications/usePdfTemplates';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/context/ToastContext';
import { PdfTemplateForm } from './PdfTemplateFormDialog';
import { PdfTemplateVersionHistoryPanel } from './PdfTemplateVersionHistoryPanel';
import { PdfTemplateVersionPickerDropdown } from './PdfTemplateVersionPickerDropdown';
import type { PdfTemplateResponse } from '@erp/rpc';

const pdfTemplateLayoutRoute = getRouteApi(
  '/authenticated/settings/organization/pdf-templates/$templateId',
);

interface PdfTemplateEditorPageProps {
  viewingVersionNumber?: number | null;
}

export function PdfTemplateEditorPage({
  viewingVersionNumber,
}: PdfTemplateEditorPageProps = {}) {
  const navigate = useNavigate();
  const gt = useGT();
  const { showToast } = useToast();
  const { templateId } = pdfTemplateLayoutRoute.useParams();
  const isCreateMode = templateId === 'new';
  const { data, isPending, error } = usePdfTemplates();
  const template = data?.templates.find(
    (candidate) => candidate.id === templateId,
  );

  const { data: versionsData, isPending: versionsPending } =
    usePdfTemplateVersions(isCreateMode ? '' : templateId);
  const versions = versionsData?.versions ?? [];

  const viewingVersion =
    viewingVersionNumber != null
      ? versions.find((v) => v.version === viewingVersionNumber)
      : null;

  const versionNotFound =
    viewingVersionNumber != null && versions.length > 0 && !viewingVersion;

  const currentVersion = viewingVersion
    ? viewingVersion
    : (versions.find((v) => v.id === template?.pinnedVersionId) ?? versions[0]);

  const isViewingDraft = currentVersion?.status === 'draft';
  const isViewingOldVersion =
    viewingVersion != null && viewingVersion.id !== template?.pinnedVersionId;

  const templateForForm: PdfTemplateResponse | undefined =
    viewingVersion && template
      ? {
          ...template,
          jsonSpec: viewingVersion.jsonSpec,
          variables: viewingVersion.variables ?? [],
          pageSetup: viewingVersion.pageSetup,
        }
      : template;

  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);

  const isEditing = isCreateMode || isViewingDraft;
  const readOnly = !isEditing;

  const createDraft = useCreatePdfTemplateDraft();

  async function handleEdit() {
    if (!template) return;

    const existingDraft = versions.find((v) => v.status === 'draft');
    if (existingDraft) {
      navigate({
        to: '/settings/organization/pdf-templates/$templateId/v/$versionNumber',
        params: { templateId, versionNumber: String(existingDraft.version) },
      });
      return;
    }

    try {
      const { version } = await createDraft.mutateAsync({
        pdfTemplateId: template.id,
      });
      navigate({
        to: '/settings/organization/pdf-templates/$templateId/v/$versionNumber',
        params: { templateId, versionNumber: String(version.version) },
      });
    } catch (err) {
      showToast({
        title: gt('Failed to create draft'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    }
  }

  function returnToList() {
    navigate({ to: '/settings/organization/pdf-templates' });
  }

  function handleSaved() {
    navigate({
      to: '/settings/organization/pdf-templates/$templateId',
      params: { templateId },
    });
  }

  function handleCancelEdit() {
    navigate({
      to: '/settings/organization/pdf-templates/$templateId',
      params: { templateId },
    });
  }

  if ((isPending || versionsPending) && !isCreateMode) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-muted-foreground">
          <T context="pdf template editor loading state">
            Loading PDF template...
          </T>
        </span>
      </div>
    );
  }

  if (error && !isCreateMode) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-destructive">
        <T context="pdf template editor load error">
          Error loading PDF template. Please return to the templates list and
          try again.
        </T>
      </div>
    );
  }

  if (!isCreateMode && !template) {
    return (
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <Button variant="ghost" size="sm" onClick={returnToList}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          <T context="pdf template editor back button">Back to templates</T>
        </Button>
        <p className="text-sm text-muted-foreground">
          <T context="pdf template editor not found message">
            PDF template <Var>{templateId}</Var> was not found.
          </T>
        </p>
      </div>
    );
  }

  if (versionNotFound) {
    return (
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <Button variant="ghost" size="sm" onClick={returnToList}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          <T context="pdf template editor back button">Back to templates</T>
        </Button>
        <p className="text-sm text-muted-foreground">
          <T context="pdf template version not found message">
            Version <Var>{viewingVersionNumber}</Var> was not found for this
            template.
          </T>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={returnToList}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            <T context="pdf template editor back button">Back to templates</T>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {!isCreateMode && template && currentVersion && (
            <PdfTemplateVersionPickerDropdown
              templateId={template.id}
              pinnedVersionId={template.pinnedVersionId}
              currentVersionNumber={currentVersion.version}
              currentVersionId={currentVersion.id}
              currentStatus={currentVersion.status}
              currentIsPinned={currentVersion.id === template.pinnedVersionId}
              onOpenFullHistory={() => setIsVersionHistoryOpen(true)}
            />
          )}

          {readOnly && !isViewingOldVersion && template && (
            <Button
              size="sm"
              onClick={handleEdit}
              disabled={createDraft.isPending}
            >
              <PencilSimpleIcon className="mr-1.5 h-4 w-4" />
              {createDraft.isPending ? (
                <T>Creating draft...</T>
              ) : versions.some((v) => v.status === 'draft') ? (
                <T>Resume draft</T>
              ) : (
                <T>Edit</T>
              )}
            </Button>
          )}
        </div>
      </div>

      {isViewingOldVersion && viewingVersion && !isViewingDraft && (
        <AlertContainer>
          <AlertDescription>
            <T>
              You are viewing v<Var>{viewingVersion.version}</Var>, which is not
              the published version.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}

      {isViewingDraft && (
        <AlertContainer>
          <AlertDescription>
            <T>
              You are editing a draft (v<Var>{currentVersion.version}</Var>).
              Save as draft or publish when ready.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}

      <div className="rounded-lg border bg-card p-4">
        <PdfTemplateForm
          key={viewingVersion?.id ?? template?.id}
          template={templateForForm}
          draftVersionId={isViewingDraft ? currentVersion?.id : null}
          onCancel={isCreateMode ? returnToList : handleCancelEdit}
          onSaved={isCreateMode ? returnToList : handleSaved}
          expandedJsonEditor
          readOnly={readOnly}
        />
      </div>

      {!isCreateMode && template && (
        <PdfTemplateVersionHistoryPanel
          open={isVersionHistoryOpen}
          onClose={() => setIsVersionHistoryOpen(false)}
          pdfTemplateId={template.id}
          pinnedVersionId={template.pinnedVersionId}
        />
      )}
    </div>
  );
}
